import Styles from './PostStatusBadge.module.scss'
import cx from 'classnames'
import { AlmanacFileCalendarEvent } from 'screens/Calendar/types'
import { PostStatusBadge as PostStatusBadgeName } from 'hooks/queries/calendar/types'
import { Tooltip } from 'components/Tooltip/Tooltip'

import { StatusPill } from './StatusPill'
import { AlertTriangle } from 'blaze-ui/icons'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { useValidationErrors } from 'hooks/calendar/useValidationErrors'
import { useModals } from 'almanac-ui/Modal'
import { ActivationConnectModal } from 'components/ActivationConnectModal'

export interface PostStatusBadgeV2Props {
  event: AlmanacFileCalendarEvent
  size?: 'xs' | 'sm' | 'md'
}

type StatusPillConfig = {
  testId: string
  status: string
  postStatus:
    | 'draft'
    | 'review'
    | 'connect'
    | 'scheduled'
    | 'approved'
    | 'posted'
    | 'failed'
    | 'paused'
    | 'updating'
    | 'new'
  color: string
  fontColor: string
}

// Per-badge pill rendering recipe. Mirrors the inline JSX in the legacy
// PostStatusBadge so the rendered output is identical when the back end
// resolves the same `badge` value the front end used to derive locally.
// Some color tokens differ between the system-redesign and legacy themes,
// hence the function shape.
const buildStatusPillConfigs = (): Record<PostStatusBadgeName, StatusPillConfig> => ({
  draft: {
    testId: 'draft-badge',
    status: 'Draft',
    postStatus: 'draft',
    color: '#EAEDF1',
    fontColor: 'rgba(52, 64, 84, 1)',
  },
  new: {
    testId: 'new-badge',
    status: 'New',
    postStatus: 'new',
    color: '#FCE6FE',
    fontColor: '#A229FE',
  },
  failed: {
    testId: 'failed-badge',
    status: 'Failed',
    postStatus: 'failed',
    color: '#FDECEC',
    fontColor: 'var(--red-70)',
  },
  posted: {
    testId: 'posted-badge',
    status: 'Posted',
    postStatus: 'posted',
    color: '#ECFDF3',
    fontColor: 'rgba(7, 148, 42, 1)',
  },
  updating: {
    testId: 'updating-badge',
    status: 'Updating',
    postStatus: 'updating',
    color: 'rgba(177, 190, 205, 1)',
    fontColor: 'var(--dark-80)',
  },
  review: {
    testId: 'review-badge',
    status: 'Review',
    postStatus: 'review',
    color: '#F8F0FF',
    fontColor: '#A229FE',
  },
  scheduled: {
    testId: 'scheduled-badge',
    status: 'Scheduled',
    postStatus: 'scheduled',
    color: '#F8F0FF',
    fontColor: '#A229FE',
  },
  approved: {
    testId: 'approved-badge',
    status: 'Approved',
    postStatus: 'approved',
    color: '#EFF7FF',
    fontColor: '#157EEF',
  },
  paused: {
    testId: 'paused-badge',
    status: 'Paused',
    postStatus: 'paused',
    color: '#EAEDF1',
    fontColor: '#344054',
  },
})

/**
 * Back-end-driven status badge. Reads `event.post_status_badge` (resolved
 * by AlmanacFileIndexItemSerializer) and renders the resulting pill,
 * warning icon, and hybrid-alert overlay.
 *
 * Platform-level validation errors (content length, file size, video
 * duration, etc.) still live on the front end and are merged into:
 *   - the warning icon visibility (`show_warning ||
 *     (Article + validationErrors)`), and
 *   - the tooltip fallback chains (`error_message || validationErrors ||
 *     <back-end tooltip>`).
 *
 * Call sites should fall back to the legacy <PostStatusBadge /> whenever
 * the Flipper flag is off OR `event.post_status_badge` is missing.
 */
export const PostStatusBadgeV2 = ({ event, size = 'md' }: PostStatusBadgeV2Props) => {
  const { scheduled_post, scheduled_post_targets } = event
  const { openModal } = useModals()
  const { data: brandPlan } = useCurrentBrandPlan()
  const isV2 = brandPlan?.has_content_strategies
  const { validationErrors } = useValidationErrors({ event })

  const badgeData = event.post_status_badge

  if (!badgeData) return null

  const { badge, show_warning, show_hybrid_alert, hybrid_alert_tooltip, warning_tooltip, badge_tooltip } = badgeData
  const pillConfig = badge ? buildStatusPillConfigs()[badge] : null

  // Front-end-owned platform validation errors get merged into the warning
  // icon visibility and the tooltip fallback chains below.
  const hasArticleValidationErrors = event.asset_type === 'Article' && validationErrors.length > 0
  const showWarning = show_warning || hasArticleValidationErrors

  const hasAtLeastOneConnectedTarget = !!scheduled_post_targets?.some((target) => !!target.external_integration_id)
  const handleClick = () => {
    if (hasAtLeastOneConnectedTarget) return

    openModal(ActivationConnectModal, {}, { newStack: true })
  }

  const failedTooltipLabel = scheduled_post?.error_message || validationErrors.join('\n') || badge_tooltip
  const warningTooltipLabel = scheduled_post?.error_message || validationErrors.join('\n') || warning_tooltip

  let alertIconSizeV2 = 18
  if (size === 'xs') {
    alertIconSizeV2 = 14
  } else if (size === 'sm') {
    alertIconSizeV2 = 16
  }

  return (
    <div className={Styles.pillsContainer}>
      {pillConfig && badge === 'failed' && (
        <Tooltip label={failedTooltipLabel} placement="top" hide={!failedTooltipLabel}>
          <div>
            <StatusPill {...pillConfig} size={size} />
          </div>
        </Tooltip>
      )}
      {pillConfig && badge !== 'failed' && <StatusPill {...pillConfig} size={size} />}
      {showWarning && (
        <Tooltip label={warningTooltipLabel} placement="top" hide={!warningTooltipLabel}>
          <div
            className={cx(Styles.statusCornerContainer, { [Styles.clickable]: !hasAtLeastOneConnectedTarget })}
            onClick={handleClick}
          >
            <div className={cx(Styles.warningPill, Styles[size], { [Styles.isV2]: isV2 })} data-testid="warning-badge">
              <AlertTriangle size={alertIconSizeV2} color="var(--dark-80)" />
            </div>
          </div>
        </Tooltip>
      )}
      {show_hybrid_alert && (
        <Tooltip label={hybrid_alert_tooltip || ''} placement="top">
          <div
            className={cx(Styles.hybridAlert, Styles[size], { [Styles.isV2]: isV2 })}
            data-testid="hybrid-warning-badge"
          >
            <AlertTriangle size={alertIconSizeV2} color="var(--light-100)" />
          </div>
        </Tooltip>
      )}
    </div>
  )
}
